import { PlatformMessage } from '@reachmedianetwork/post-message-client-rds-api';
import { PlayerPmc } from '../PlayerPmc';
import { JsonValue } from '../../Utility/JsonValue';

export type WindowRectangle = {
    top: number;
    left: number;
    width: number;
    height: number;
};

type HandleId = number;

export interface MultiWindowInterface {
    /**
     * Creates another window on the screen.
     * Returns a handle id of the newly created window.
     * The handle id will be -1 if no window was created.
     */
    createWindow(options: {
        rectangle: WindowRectangle;
        url: string;
    }): Promise<HandleId> | HandleId;
    /**
     * Tries to destroy the window.
     * Will return false if the underlying platform couldn't find the referenced handle.
     */
    destroyWindow(handleId: HandleId): Promise<boolean> | boolean;
}

export interface ExecuteJavascriptOnWindow {
    /**
     * This method returns the result from executing the provided JavaScript.
     * The implementer is responsible for making sure the result is parsed.
     *
     * @throws Error if handleId doesn't exist or another error occurs.
     */
    executeJsOnWindow(handleId: HandleId, jsToExecute: string): Promise<JsonValue> | JsonValue;
}

export function supportsExecuteJavascriptOnWindow(
    platform: unknown
): platform is ExecuteJavascriptOnWindow {
    if (platform instanceof PlayerPmc) {
        return platform.supportsMessage(PlatformMessage.ExecuteJavascriptOnWindow);
    }
    return platform instanceof Object && 'executeJsOnWindow' in platform;
}

export function supportsMultiWindow(platform: unknown): platform is MultiWindowInterface {
    if (platform instanceof PlayerPmc) {
        return (
            platform.supportsMessage(PlatformMessage.CreateWindow) &&
            platform.supportsMessage(PlatformMessage.DestroyWindow)
        );
    }
    return platform instanceof Object && 'createWindow' in platform && 'destroyWindow' in platform;
}
